import React, { useEffect, useState, useRef } from "react";
import ReactAudioPlayer from "react-audio-player";
import { talkgroupNameFor, groupForTalkgroup } from "./demoHelpers";
import { useCallLink } from "../Call/components/CallLinks";
import Waveform from "./Waveform";
import DemoDetail from "./DemoDetail";

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

// The chosen speed is remembered across visits (same key as the classic player).
const loadSavedRate = () => {
  const saved = parseFloat(localStorage.getItem("playbackRate"));
  return SPEED_OPTIONS.includes(saved) ? saved : 1;
};

const fmt = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec < 10 ? "0" : ""}${sec}`;
};

function DemoPlayer({
  call, playSilence, autoPlay, onAutoPlayToggle, onEnded, onPlayPause, onNext, onPrev,
  talkgroupsData, groupsData, callsData,
}) {
  const audioRef = useRef();
  const [sourceIndex, setSourceIndex] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(loadSavedRate);
  const [playProgress, setPlayProgress] = useState(0);
  const [playTime, setPlayTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { callDownload } = useCallLink(call || false);

  const copyLink = () => {
    if (!callDownload || !navigator.clipboard) return;
    navigator.clipboard.writeText(callDownload).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  const handlePause = () => setIsPlaying(false);
  const handlePlay = () => setIsPlaying(true);
  const playPause = () => {
    const audio = audioRef.current.audioEl.current;
    if (isPlaying) { setIsPlaying(false); onPlayPause(false); audio.pause(); }
    else { setIsPlaying(true); onPlayPause(true); audio.play(); }
  };

  // Apply the playback speed and remember it. defaultPlaybackRate is set too so
  // browsers that reset the rate when a new src loads come back to this speed.
  useEffect(() => {
    const audio = audioRef.current.audioEl.current;
    audio.defaultPlaybackRate = playbackRate;
    audio.playbackRate = playbackRate;
    localStorage.setItem("playbackRate", playbackRate);
  }, [playbackRate]);

  // Load and play the selected call. (Ported from MediaPlayer.js)
  useEffect(() => {
    const audio = audioRef.current.audioEl.current;
    setSourceIndex(0);
    setPlayProgress(0);
    setPlayTime(0);
    if (call) {
      audio.src = call.url;
      audio.playbackRate = playbackRate;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.then(function () {}).catch(function (error) {
          console.log("Automatic playback failed: " + error);
          handlePause();
        });
      } else {
        audio.src = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call]);

  // Background-autoplay keep-alive: play a snippet of silence so the tab is not
  // put to sleep when there are no more calls. (Ported from MediaPlayer.js)
  useEffect(() => {
    const audio = audioRef.current.audioEl.current;
    setSourceIndex(0);
    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: "Waiting for Call...",
        album: "OpenMHz",
        artwork: [
          { src: "/favicon-32x32.png", sizes: "32x32", type: "image/png" },
          { src: "/android-chrome-192x192.png", sizes: "192x192", type: "image/png" },
          { src: "/android-chrome-192x192.png", sizes: "512x512", type: "image/png" },
        ],
      });
    }
    audio.src = "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA/+M4wAAAAAAAAAAAAEluZm8AAAAPAAAAAwAAAbAAqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV////////////////////////////////////////////AAAAAExhdmM1OC4xMwAAAAAAAAAAAAAAACQDkAAAAAAAAAGw9wrNaQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+MYxAAAAANIAAAAAExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/+MYxDsAAANIAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/+MYxHYAAANIAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.then(function () {}).catch(function (error) {
        console.log("Automatic playback failed: " + error);
      });
    } else {
      audio.src = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playSilence]);

  const updatePlayProgress = () => {
    const audio = audioRef.current.audioEl.current;
    const { currentTime, duration: dur } = audio;
    if (call && call.srcList.length - 1 >= sourceIndex + 1 && currentTime > call.srcList[sourceIndex + 1].pos) {
      setSourceIndex(sourceIndex + 1);
    }
    if (dur && dur !== duration) setDuration(dur);
    setPlayProgress((currentTime / dur) * 100);
    setPlayTime(currentTime);
  };

  const handleSeek = (pct) => {
    const audio = audioRef.current.audioEl.current;
    const dur = audio.duration;
    if (dur && isFinite(dur)) {
      audio.currentTime = (pct / 100) * dur;
      setPlayProgress(pct);
      setPlayTime(audio.currentTime);
    }
  };

  const selectSpeed = (r) => { setPlaybackRate(r); setSpeedOpen(false); };

  let sourceId = "—";
  if (call && call.srcList.length > sourceIndex) {
    const s = call.srcList[sourceIndex];
    sourceId = s.tag && s.tag !== "N/A" ? s.tag : s.src;
  }
  const tgName = call ? talkgroupNameFor(call.talkgroupNum, talkgroupsData) : "Waiting for a call";
  const group = call ? groupForTalkgroup(call.talkgroupNum, groupsData) : null;
  const total = duration || (call ? call.len : 0);

  // per-transmission ticks as % of the call length
  const segments = (call && call.srcList ? call.srcList : [])
    .map((s) => (total ? (s.pos / total) * 100 : 0))
    .filter((p) => p > 1 && p < 99);

  return (
    <>
      {expanded && <div className="demo-sheet-scrim" onClick={() => setExpanded(false)} />}
      <div className={"demo-player" + (expanded ? " expanded" : "")}>
        {expanded && (
          <DemoDetail
            call={call}
            callsData={callsData}
            talkgroupsData={talkgroupsData}
            groupsData={groupsData}
          />
        )}
        <div className="demo-console">
          <ReactAudioPlayer ref={audioRef} onPause={handlePause} onPlay={handlePlay}
            listenInterval={100} onListen={updatePlayProgress} onEnded={onEnded} autoPlay />

          <div className="demo-transport">
            <button className="demo-t-btn demo-t-prev" onClick={onPrev} disabled={!call} title="Previous call" aria-label="Previous call">⏮</button>
            <button className="demo-t-btn demo-t-play" onClick={playPause} disabled={!call} aria-label={isPlaying ? "Pause" : "Play"}>
              {isPlaying ? "❚❚" : "▶"}
            </button>
            <button className="demo-t-btn demo-t-next" onClick={onNext} disabled={!call} title="Next call" aria-label="Next call">⏭</button>
          </div>

          <div className="demo-now">
            <div className="demo-now-group">{group ? group.groupName : (call ? "Ungrouped" : "Idle")}</div>
            <div className={"demo-now-tg" + (call ? "" : " idle")}>{tgName}</div>
            <div className="demo-now-sub demo-mono">
              {call ? <>unit <span className="demo-unit">{sourceId}</span></> : "Pick a call, or wait for the next transmission"}
            </div>
          </div>

          <div className="demo-wave-region">
            <Waveform progress={playProgress} playing={isPlaying} segments={segments} onSeek={call ? handleSeek : null} />
            <div className="demo-clock">
              {fmt(playTime)} <span className="demo-clock-total">/ {fmt(total)}</span>
            </div>
          </div>

          <div className="demo-quick">
            <button className={"demo-quick-btn" + (copied ? " copied" : "")} onClick={copyLink} disabled={!call}
              title="Copy link" aria-label={copied ? "Link copied" : "Copy link"}>{copied ? "✓" : "🔗"}</button>
            {call
              ? <a className="demo-quick-btn" href={callDownload} download title="Download" aria-label="Download">⬇</a>
              : <button className="demo-quick-btn" disabled title="Download" aria-label="Download">⬇</button>}
          </div>

          <div className="demo-controls">
            <div className="demo-speed">
              <button className="demo-speed-btn" onClick={() => setSpeedOpen((o) => !o)} title="Playback speed">{playbackRate}×</button>
              {speedOpen && (
                <div className="demo-speed-menu">
                  {SPEED_OPTIONS.map((r) => (
                    <button key={r} className={r === playbackRate ? "active" : ""} onClick={() => selectSpeed(r)}>{r}×</button>
                  ))}
                </div>
              )}
            </div>
            <div className={"demo-auto" + (autoPlay ? " on" : "")} onClick={onAutoPlayToggle} role="switch" aria-checked={autoPlay} tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAutoPlayToggle(); } }}>
              <span className="demo-switch" /><span className="demo-auto-label">Autoplay</span>
            </div>
            <button className="demo-expand" onClick={() => setExpanded((v) => !v)} title={expanded ? "Collapse" : "Call details"} aria-label="Call details">
              {expanded ? "▾" : "▴"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default DemoPlayer;
